import * as React from "react";
import { FlaskConical, Gauge, Gem } from "lucide-react";

import { ApiError, disableUser, enableUser, listAdminUsers, setTokenCap } from "@/api/client";
import type { AdminUserView, ApprovalStatus } from "@/api/types";
import { NavIconLink } from "@/features/home/components/NavIcons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ComicPanel } from "@/components/comic";
import { PageTitle } from "@/components/PageTitle";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useProfile } from "../profile/ProfileContext";

/**
 * Admin-only directory of every signed-up user. Beyond listing, admins can
 * enable/disable any non-admin account (the login-conditional approval gate)
 * and click a row to view that user's full profile read-only. The page guards
 * itself on {@code profile.admin}, but the backend's admin_user allowlist is
 * the real authority — a hand-typed /admin by a non-admin gets the 403 panel.
 */
export function AdminPage() {
  const { state } = useProfile();
  const profile = state.status === "ready" ? state.profile : null;

  const [users, setUsers] = React.useState<AdminUserView[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const isAdmin = profile?.admin === true;
  const wantsIn = (users ?? []).filter((u) => u.interestCount > 0);

  React.useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    listAdminUsers()
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 403
            ? "Your account is not on the admin allowlist."
            : err instanceof Error
              ? err.message
              : String(err),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  async function applyAction(userId: string, enable: boolean) {
    setBusyId(userId);
    setActionError(null);
    try {
      const updated = enable ? await enableUser(userId) : await disableUser(userId);
      setUsers((prev) =>
        prev ? prev.map((u) => (u.id === userId ? updated : u)) : prev,
      );
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.status === 409
          ? "Admins cannot be disabled."
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function applyCap(
    userId: string,
    dailyLimit: number | null,
    monthlyLimit: number | null,
  ) {
    setActionError(null);
    try {
      const updated = await setTokenCap(userId, { dailyLimit, monthlyLimit });
      setUsers((prev) =>
        prev ? prev.map((u) => (u.id === userId ? updated : u)) : prev,
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-4 py-6 font-comic-body">
      <header className="flex items-start justify-between gap-3">
        <div>
          <PageTitle>Admin console</PageTitle>
          <p className="text-xs text-muted-foreground">
            Everyone who has signed up, newest first, including those who
            haven't sent a preview note. Click a handle to open that user's
            profile (read-only, new tab); enable or disable access from the
            Actions column.
          </p>
          <nav className="mt-2 flex items-center gap-1">
            <NavIconLink to="/admin/llm" label="LLM playground" color="text-brand-blue">
              <FlaskConical size={20} />
            </NavIconLink>
            <NavIconLink to="/admin/plans" label="Plans & limits" color="text-brand-teal">
              <Gem size={20} />
            </NavIconLink>
            <NavIconLink
              to="https://us-east-1.console.aws.amazon.com/cloudwatch/home?region=us-east-1#dashboards:"
              label="AWS dashboard"
              color="text-orange-500"
              external
            >
              <Gauge size={20} />
            </NavIconLink>
          </nav>
        </div>
      </header>

      {actionError && (
        <div className="rounded-md border-2 border-black bg-red-100 px-3 py-2 text-sm text-red-900">
          {actionError}
        </div>
      )}

      {isAdmin && wantsIn.length > 0 && (
        <ComicPanel title="Wants in" loud={String(wantsIn.length)}>
          <ul className="space-y-3">
            {wantsIn.map((u) => (
              <li
                key={u.id}
                className="flex items-start justify-between gap-4 rounded-md border-2 border-black bg-muted px-3 py-2"
              >
                <div className="min-w-0">
                  <a
                    href={`/admin/users/${u.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    {u.handle ? `@${u.handle}` : u.email}
                  </a>
                  <span className="ml-2">
                    <StatusBadge status={u.approvalStatus} />
                  </span>
                  {u.latestInterest && (
                    <p className="mt-1 text-sm leading-snug">
                      “{u.latestInterest}”
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {`${u.interestCount} note${u.interestCount === 1 ? "" : "s"}` +
                      `${u.latestInterestAt ? ` · ${formatWhen(u.latestInterestAt)}` : ""} · `}
                    <a
                      href={`/admin/users/${u.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-foreground"
                    >
                      view all
                    </a>
                  </p>
                </div>
                {u.approvalStatus !== "approved" && !u.admin && (
                  <Button
                    size="sm"
                    disabled={busyId === u.id}
                    onClick={() => applyAction(u.id, true)}
                  >
                    {busyId === u.id ? "…" : "Enable"}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </ComicPanel>
      )}

      {profile && !isAdmin ? (
        <ComicPanel title="Not authorized">
          <p className="text-sm text-muted-foreground">
            This page is for admins only. If you should have access, an
            existing admin can add you to the allowlist.
          </p>
        </ComicPanel>
      ) : error ? (
        <ComicPanel title="Error">
          <p className="text-sm text-destructive">{error}</p>
        </ComicPanel>
      ) : !users ? (
        <p className="text-sm text-muted-foreground">Loading users…</p>
      ) : (
        <ComicPanel title="Signed-up users" loud={String(users.length)} dense>
          <p className="mb-2 text-[11px] text-muted-foreground">
            The complete list of everyone who has opened the app. “Wants in”
            above is just the subset who also sent a preview note.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Handle</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Signed up</TableHead>
                <TableHead>Tokens (today · month)</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <a
                      href={`/admin/users/${u.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-primary hover:underline"
                    >
                      {u.handle ? `@${u.handle}` : (u.email ?? "view profile")}
                    </a>
                    {u.admin && (
                      <Badge variant="secondary" className="ml-2">
                        admin
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{u.displayName ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.email}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={u.approvalStatus} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatSignupDate(u.createdAt)}
                  </TableCell>
                  <TableCell>
                    <TokenCell
                      user={u}
                      onSave={(daily, monthly) => applyCap(u.id, daily, monthly)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <RowAction
                      user={u}
                      busy={busyId === u.id}
                      onEnable={() => applyAction(u.id, true)}
                      onDisable={() => applyAction(u.id, false)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ComicPanel>
      )}
    </div>
  );
}

function RowAction({
  user,
  busy,
  onEnable,
  onDisable,
}: {
  user: AdminUserView;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
}) {
  if (user.admin) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const approved = user.approvalStatus === "approved";
  return (
    <Button
      variant={approved ? "destructive" : "default"}
      size="sm"
      disabled={busy}
      onClick={() => (approved ? onDisable() : onEnable())}
    >
      {busy ? "…" : approved ? "Disable" : "Enable"}
    </Button>
  );
}

/** Compact token count: 1234 → "1.2k", 8000 → "8k", 2_000_000 → "2M". */
function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

/**
 * Per-user token usage (today · month) and the admin-set caps, with an inline
 * editor. Blank or non-positive inputs clear a cap (→ unlimited, shown as ∞).
 */
function TokenCell({
  user,
  onSave,
}: {
  user: AdminUserView;
  onSave: (daily: number | null, monthly: number | null) => void | Promise<void>;
}) {
  const [editing, setEditing] = React.useState(false);
  const [daily, setDaily] = React.useState("");
  const [monthly, setMonthly] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setDaily(user.dailyTokenLimit?.toString() ?? "");
    setMonthly(user.monthlyTokenLimit?.toString() ?? "");
  }, [user.dailyTokenLimit, user.monthlyTokenLimit]);

  const parse = (v: string): number | null => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  async function save() {
    setSaving(true);
    try {
      await onSave(parse(daily), parse(monthly));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="number"
          min={0}
          value={daily}
          onChange={(e) => setDaily(e.target.value)}
          placeholder="daily"
          className="h-7 w-20 text-xs"
        />
        <Input
          type="number"
          min={0}
          value={monthly}
          onChange={(e) => setMonthly(e.target.value)}
          placeholder="month"
          className="h-7 w-20 text-xs"
        />
        <Button size="sm" disabled={saving} onClick={() => void save()}>
          {saving ? "…" : "Save"}
        </Button>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:underline"
          onClick={() => setEditing(false)}
        >
          Cancel
        </button>
      </div>
    );
  }

  const cap = (n: number | null) => (n == null ? "∞" : fmtTokens(n));
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className="text-xs">
        {fmtTokens(user.tokensToday)} · {fmtTokens(user.tokensMonth)}
        <span className="ml-1 text-muted-foreground">
          / {cap(user.dailyTokenLimit)} · {cap(user.monthlyTokenLimit)}
        </span>
      </span>
      <button
        type="button"
        className="text-[11px] text-primary hover:underline"
        onClick={() => setEditing(true)}
      >
        edit caps
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: ApprovalStatus }) {
  const variant =
    status === "approved" ? "default" : status === "disabled" ? "destructive" : "secondary";
  return <Badge variant={variant}>{status}</Badge>;
}

function formatSignupDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}
